import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import toast from 'react-hot-toast'
import { useShallow } from 'zustand/react/shallow'

import {
  DelimitedContent,
  FvButton,
  Modal,
  OrDivider,
  useFvNavigate,
  ValidatedForm,
} from '@fv/client-components'
import { isApiError, submitForm, useQueryClient } from '@fv/client-core'

import { FooterBar } from '@/components/FooterBar'
import { Loading } from '@/components/Loading'
import { LoadSummary } from '@/components/LoadSummary/LoadSummary'
import { supportMessage } from '@/constants'
import { useQuotes } from '@/hooks/shipments'
import { quoteKeys } from '@/hooks/shipments/useQuotes'
import { routes } from '@/routes'
import { isBookedAndDispatched } from '@/utils/shipmentFuncs'

import { addressBookKeys } from '../addresses/useAddressBookAddresses'
import { BillTo } from '../billTo/BillTo'
import { useAwardLoads } from '../booking/mutations'
import {
  LoadItemsForm,
  type LoadItemsFormRef,
} from '../commodities/load-items/LoadItemsFormProvider'
import { BrandingFormSection } from '../company-branding/BrandingSelector'
import { LTLBroker } from '../customsBroker/LTLBroker'
import { LTLContact } from '../emergencyContact/LTLContact'
import { quoteFuncs } from '../quote/quoteFuncs'
import { QuoteReasonSlider } from '../quote-reason/QuoteReasonForm'
import { ShipmentFormSection } from '../shipment-form/ShipmentFormSection'
import { getBookActionLabel } from '../shipment-form/utils'
import { buildShipmentLocation } from '../shipment-location-form/types'
import { BookingAccessorials } from './BookingAccessorials'
import { LtlBookingBolField } from './BookingFormBolField'
import { BookingFormGLCode } from './BookingFormGLCode'
import { BookingFormLocation } from './BookingFormLocation'
import { useBookingFormCtx, useBookingFormLoad } from './BookingFormProvider'
import { LtlBookingFormShare } from './BookingFormShare'
import { LtlBookingFormTags } from './BookingFormTags'
import { BookingRateDetails } from './BookingRateDetail'
import { useCarrierBookingFormSettings } from './bookFuncs'
import { CustomerRoutedForm } from './CustomerRoutedForm'
import { BookingErrorNotice } from './ErrorNotice'
import { FedExHomeDeliveryBooking } from './FedExHomeDelivery'
import { FedExShipAlert } from './FedExShipAlert'
import { HoldAtLocationForm } from './HoldAtLocationForm'
import { LTLRateLocation } from './LTLRateLocation'
import { RateChangeNotice } from './RateChangeNotice'
import { ReratingOverlay } from './ReratingOverlay'
import type { BuildAwardRequestArgs } from './types'
import { useBookingFormCanSchedulePickup } from './useCanSchedulePickup'
import { useCreateCustomerRoutedLoad } from './useCreateCustomerRouted'

export const BookingForm = () => {
  const queryClient = useQueryClient()
  const itemsFormRef = useRef<LoadItemsFormRef>(null)
  const actions = useBookingFormCtx(s => s.actions)
  const workflow = useBookingFormCtx(s => s.workflow)
  const isFreightCollect = useBookingFormCtx(s => s.isFreightCollect)
  const isParcel = workflow === 'parcel'
  const isLtl = workflow === 'ltl'
  const addCRLoad = useCreateCustomerRoutedLoad()
  const awardLoads = useAwardLoads()
  const canSchedulePickup = useBookingFormCanSchedulePickup()
  const canSchedulePickupOnly = useCanSchedulePickupOnly()
  const canDoBolOnly = useCanRequestBolOnly()
  const formRef = useRef<HTMLFormElement>(null)
  const isEdit = useBookingFormCtx(s => s.isEdit)
  const load = useBookingFormLoad()
  const navigate = useFvNavigate()
  const quote = useBookingFormCtx(s => s.quote)
  const [bookCache, setBookCache] = useState<{
    createBOL: boolean
    schedulePickup: boolean
  }>()
  const [rerating, setRerating] = useState(
    load?.selectedQuote?._id === quote?._id &&
      (load?.quoteProcessStatus === 'rerating' ||
        load?.quoteProcessStatus === 'rerate-ready'),
  )
  const { invalidate: clearQuoteCache } = useQuotes(load?.loadId, {
    enabled: false,
  })
  const isBusy =
    addCRLoad.isPending ||
    addCRLoad.isSuccess ||
    awardLoads.isPending ||
    awardLoads.isSuccess
  const [errorList, setErrorList] = useState<string[]>()
  const locations = useBookingFormCtx(s => s.locations)
  const items = useBookingFormCtx(s => s.items)
  const initialItems = useBookingFormCtx(s => s.initialItems)
  const brandId = useBookingFormCtx(s => s.brandId)
  const setBrandId = useBookingFormCtx(s => s.actions.setBrandId)
  const bookLabel = getBookActionLabel(workflow)

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: quoteKeys.rerated(load?.loadId) })
  }, [queryClient, rerating, load])

  function bookShipment({
    createBOL,
    schedulePickup,
    quoteId,
  }: BuildAwardRequestArgs) {
    setErrorList([])
    if (isBusy) return
    if (!formRef.current?.checkValidity()) {
      return submitForm(formRef.current)
    }

    const itemsValid = itemsFormRef.current?.validateItems(workflow, 'booking')
    if (!itemsValid) {
      return submitForm(formRef.current)
    }

    setBookCache({ schedulePickup: Boolean(schedulePickup), createBOL })

    if (isFreightCollect && !isEdit) {
      const dto = actions.buildCustomerRoutedDTO(createBOL, schedulePickup)

      addCRLoad.mutate(dto, {
        onError: error => {
          if (isApiError(error)) {
            setErrorList(
              error.message
                ? [error.message]
                : error.errors?.map(e => e.message),
            )
          } else {
            setErrorList([`There was an error, ${supportMessage}`])
          }
        },
        onSuccess: loads => {
          navigate(routes.details(loads[0].loadId))
        },
      })
    } else {
      const dto = actions.buildRequestDTO({
        createBOL,
        schedulePickup,
        quoteId,
      })

      awardLoads.mutate(dto, {
        onSuccess: ({ loads }) => {
          const { loadId, quoteProcessStatus, status } = loads[0]

          if (quoteProcessStatus === 'rerating') {
            awardLoads.reset()
            setRerating(true)
          } else {
            clearQuoteCache()
            queryClient.invalidateQueries({ queryKey: addressBookKeys.all })
            navigate(
              status === 'pending'
                ? routes.rates(loads[0])
                : routes.details(loadId),
            )
          }
        },
        onError: error => {
          console.warn({ error })

          const err = error as any
          let msg = err.message

          if (err.errors?.length) {
            const { propertyName, message } = err.errors[0]
            msg += ` "${propertyName}" ${message?.toLowerCase()}.`
          }

          setErrorList(msg)
          toast.error(msg)
        },
      })
    }
  }

  useEffect(() => {
    if (load?.pickup?.status === 'error') {
      setTimeout(
        // force this hook to happen after the clearing out hook
        () =>
          setErrorList([
            load?.pickup?.error || `Unable to book shipment, ${supportMessage}`,
          ]),
        0,
      )
    }
  }, [load?.loadId, load?.pickup?.error, load?.pickup?.status]) // leave load?.loadId as a dependency otherwise errors get cleared out by other hooks

  // If the customer is editing a load after booking, we want to go ahead and redirect the customer to
  // the details page once the re-rate process finishes even if no matching quote was found.
  useEffect(() => {
    if (
      (load?.quoteProcessStatus === 'rerate-no-compat' ||
        load?.quoteProcessStatus === 'ok') &&
      rerating &&
      isBookedAndDispatched(load)
    ) {
      navigate(routes.details(load.loadId))
    }
  }, [load, rerating, navigate])

  if (!load) {
    return <Loading />
  }

  return (
    <>
      <BookingFormErrorClearer setErrorList={setErrorList} />
      <LoadSummary
        load={{
          ...load,
          locations: locations.map(buildShipmentLocation),
        }}
        isEditingLoad
        onDateEdit={(sequence, date) => {
          actions.setLocationValues(sequence === 1 ? 'destination' : 'origin', {
            stopDate: date,
          })
        }}
        quote={quote}
      />

      <div className=" [scrollbar-gutter:stable] overflow-auto px-6 pb-10 pt-6">
        {!!errorList?.length && (
          <BookingErrorNotice errors={errorList} className="-mx-8 " />
        )}
        {quote && (
          <BookingRateDetails
            load={load}
            quote={quote}
            className="-mx-6 -mt-6 mb-10 "
          />
        )}

        <ValidatedForm ref={formRef}>
          <CustomerRoutedForm disabled={isBusy} />
          <div className="flex flex-col gap-y-12">
            <BookingFormLocation disabled={isBusy} type="origin" />
            <BookingFormLocation disabled={isBusy} type="destination" />
            {isParcel && <FedExHomeDeliveryBooking />}
            {isParcel && <HoldAtLocationForm />}
            <LoadItemsForm
              ref={itemsFormRef}
              step="booking"
              workflow={workflow}
              initialItems={items}
              onChange={actions.setItems}
              type="inline"
            />
            {isLtl && <BookingAccessorials disabled={isBusy} />}

            {isParcel && <FedExShipAlert />}
            {isLtl && (
              <>
                <LTLContact disabled={isBusy} />
                <LTLBroker disabled={isBusy} />
                <LTLRateLocation disabled={isBusy} />
              </>
            )}
            <BillTo disabled={isBusy} />
            <BrandingFormSection
              value={brandId}
              onChange={setBrandId}
              pillClassName="default-container !border-2"
              className="gray-container"
              title="Branding"
            />
            <ShipmentFormSection
              pillClassName="default-container !border-2"
              title="BOL Info"
              className="gray-container !mb-0"
            >
              <div className="flex gap-x-2 flex-wrap">
                {!isParcel && (
                  <LtlBookingBolField className="flex-1 max-w-64" />
                )}
                <BookingFormGLCode className="w-48" />
                <LtlBookingFormShare className="flex-1" disabled={isBusy} />
                <LtlBookingFormTags className="flex-1 b1400:basis-full" />
              </div>
            </ShipmentFormSection>
            <RateChangeNotice
              onUndo={() => {
                actions.undoChanges()
                itemsFormRef.current?.setItems(initialItems)
              }}
            />
          </div>
        </ValidatedForm>
      </div>

      <FooterBar className="col-span-full" jc>
        <DelimitedContent delimiter={<OrDivider className="mx-3" />}>
          {!isEdit && canSchedulePickupOnly && (
            <FvButton
              loading={isBusy}
              icon="check"
              theme="primary"
              onClick={() =>
                bookShipment({
                  createBOL: false,
                  schedulePickup: true,
                })
              }
            >
              Submit pickup request & upload BOL
            </FvButton>
          )}
          {isEdit && workflow !== 'parcel' && (
            <FvButton
              loading={isBusy}
              icon="check"
              theme="primary"
              fwd
              className="mr-1"
              onClick={() =>
                bookShipment({
                  createBOL: load.bol?.status !== 'not-requested',
                  schedulePickup: false,
                })
              }
            >
              Save Changes
            </FvButton>
          )}
          {!isEdit && canDoBolOnly && workflow !== 'parcel' && (
            <FvButton
              loading={isBusy}
              icon="file-contract"
              theme="default"
              onClick={() =>
                bookShipment({
                  createBOL: true,
                  schedulePickup: false,
                })
              }
            >
              Print BOL only
            </FvButton>
          )}
          {!isEdit && canSchedulePickup && workflow !== 'parcel' && (
            <FvButton
              theme="primary"
              fwd
              icon={isBusy ? 'spinner' : 'truck'}
              onClick={() =>
                bookShipment({
                  createBOL: true,
                  schedulePickup: true,
                })
              }
            >
              {bookLabel}
            </FvButton>
          )}
          {workflow === 'parcel' && (
            <FvButton
              fwd
              icon={isBusy ? 'spinner' : 'truck'}
              onClick={() =>
                bookShipment({
                  createBOL: true,
                  schedulePickup: load.schedulePickup,
                })
              }
              theme="primary"
            >
              {bookLabel}
            </FvButton>
          )}
          {isEdit && (
            <FvButton icon="times" theme="default" onClick={() => navigate(-1)}>
              Cancel
            </FvButton>
          )}
        </DelimitedContent>
      </FooterBar>

      {rerating && (
        <Modal>
          <ReratingOverlay
            loadId={load.loadId}
            onAcceptRerate={quoteId => {
              clearQuoteCache()
              if (bookCache) {
                bookShipment({
                  ...bookCache,
                  quoteId,
                })
              } else {
                setRerating(false)
                navigate(routes.book({ loadId: load.loadId, quoteId }), {
                  replace: true,
                })
              }
            }}
            onDeclineRerate={() => {
              setRerating(false)
            }}
          />
        </Modal>
      )}
      {quote && load && <QuoteReasonSlider load={load} quote={quote} />}
    </>
  )
}

function useCanRequestBolOnly() {
  const carrierSetting = useCarrierBookingFormSettings()
  const isFreightCollect = useBookingFormCtx(s => s.isFreightCollect)
  if (isFreightCollect) return true
  return carrierSetting?.allowBolOnly
}

function useCanSchedulePickupOnly() {
  const selectedQuote = useBookingFormCtx(s => s.quote)
  return quoteFuncs.isSpotWorkflow(selectedQuote)
}

const emptyErrors: string[] = []
const ERROR_CLEAR_GRACE_MS = 5000

function BookingFormErrorClearer({
  setErrorList,
}: {
  setErrorList: Dispatch<SetStateAction<string[] | undefined>>
}): null {
  const canClearErrorsRef = useRef(false)
  const {
    billTo,
    bolNumber,
    locations,
    shareEmails,
    tags,
    items,
    equipmentAccessorials,
    glCode,
  } = useBookingFormCtx(
    useShallow(s => ({
      billTo: s.billTo,
      bolNumber: s.bolNumber,
      locations: s.locations,
      shareEmails: s.shareEmails,
      tags: s.tags,
      items: s.items,
      equipmentAccessorials: s.equipmentAccessorials,
      glCode: s.glCode,
    })),
  )

  useEffect(() => {
    // give a grace period for our lazily loaded location/bill-to information to not clear errors inadvertently
    const timeout = setTimeout(() => {
      canClearErrorsRef.current = true
    }, ERROR_CLEAR_GRACE_MS)
    return () => clearTimeout(timeout)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset errors when any form field changes
  useEffect(() => {
    if (!canClearErrorsRef.current) return
    setErrorList(prev => (prev?.length ? emptyErrors : prev))
  }, [
    billTo,
    bolNumber,
    locations,
    shareEmails,
    tags,
    items,
    equipmentAccessorials,
    glCode,
    setErrorList,
  ])

  return null
}
